import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { ArrowUpRight, Filter, Sparkles, Layers, Play } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/seo/Seo";
import { GlowOrb } from "@/components/common/GlowOrb";

function CategoryPill({ label, active, onClick, count, index }) {
  return (
    <button
      onClick={onClick}
      data-testid={`portfolio-cat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={`relative shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
        active ? "text-primary-foreground" : "text-foreground/70 hover:text-foreground"
      }`}
    >
      {active && (
        <motion.span
          layoutId="portfolio-cat-active"
          className="absolute inset-0 rounded-full bg-primary shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.75)]"
          transition={{ type: "spring", stiffness: 380, damping: 32 }}
        />
      )}
      <span className="relative z-10 inline-flex items-center gap-2">
        <span className="opacity-60 font-mono text-[10px]">{String(index).padStart(2, "0")}</span>
        {label}
        <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active ? "bg-black/25" : "bg-foreground/10"}`}>{count}</span>
      </span>
    </button>
  );
}

function PortfolioCard({ item, categoryName }) {
  const hasVideo = !!item.video_url;
  const isPortrait = item.media_format === "portrait";
  const to = `/portfolio/${item.slug}`;

  // Portrait phone-frame variant — used for Mobile Applications and Social Media reels
  if (isPortrait) {
    return (
      <motion.div
        layout
        initial={{ opacity: 0, y: 22, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.98 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="group"
        data-testid={`portfolio-card-${item.id}`}
        data-format="portrait"
      >
        <Link
          to={to}
          className="block w-full text-left rounded-3xl border border-border/60 bg-gradient-to-b from-card/90 to-card/40 backdrop-blur-xl hover:border-primary/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_35px_80px_-40px_hsl(var(--primary)/0.6)] p-5 pb-4"
        >
          {/* Top meta row */}
          <div className="flex items-center justify-between gap-2 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-widest text-primary bg-primary/10 border border-primary/20 px-2 py-1 rounded-full">
              {categoryName}
            </span>
            {item.year && (
              <span className="text-[10px] font-mono text-muted-foreground">{item.year}</span>
            )}
          </div>

          {/* Phone frame */}
          <div className="relative mx-auto max-w-[220px]">
            <div aria-hidden className="absolute -inset-4 rounded-[48px] bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative rounded-[38px] p-[6px] bg-gradient-to-b from-neutral-800 to-neutral-950 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),_0_25px_60px_-30px_rgba(0,0,0,0.75)]">
              <div className="relative rounded-[32px] overflow-hidden bg-black" style={{ aspectRatio: "9 / 19.5" }}>
                <img
                  src={item.cover_image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover transform-gpu group-hover:scale-105 transition-transform duration-[900ms]"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <div aria-hidden className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-4 rounded-full bg-black/90 z-10" />
                <div aria-hidden className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/0 via-white/[0.04] to-white/0" />
                {hasVideo && (
                  <>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    <div className="absolute inset-0 z-[6] grid place-items-center pointer-events-none">
                      <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-[0_15px_35px_-10px_hsl(var(--primary)/0.9)] group-hover:scale-110 transition-transform duration-500">
                        <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                        <Play className="w-5 h-5 fill-current relative z-10 translate-x-0.5" />
                      </span>
                    </div>
                  </>
                )}
                <div aria-hidden className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-14 h-1 rounded-full bg-white/40 z-10" />
              </div>
            </div>
          </div>

          {/* Title + meta below phone */}
          <div className="mt-5 text-center">
            <div className="font-heading font-bold text-base leading-tight line-clamp-2">{item.title}</div>
            {item.subtitle && (
              <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{item.subtitle}</div>
            )}
            <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-primary opacity-70 group-hover:opacity-100 transition-opacity">
              {hasVideo ? "Tap to open reel" : "View details"} <ArrowUpRight className="w-3 h-3" />
            </div>
          </div>
        </Link>
      </motion.div>
    );
  }

  // Landscape variant (default)
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 22, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      data-testid={`portfolio-card-${item.id}`}
      data-format="landscape"
    >
      <Link
        to={to}
        className="block w-full text-left relative rounded-3xl overflow-hidden border border-border/60 bg-card/70 backdrop-blur-xl hover:border-primary/40 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_35px_80px_-40px_hsl(var(--primary)/0.6)]"
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary/15 to-secondary/15">
          <img
            src={item.cover_image}
            alt={item.title}
            className="w-full h-full object-cover transform-gpu group-hover:scale-110 transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-70 group-hover:opacity-85 transition-opacity duration-500" />
          <div className="absolute top-4 left-4 z-10">
            <span className="text-[10px] font-mono uppercase tracking-widest bg-white/10 backdrop-blur-md text-white px-2.5 py-1 rounded-full border border-white/25">
              {categoryName}
            </span>
          </div>
          {item.year && (
            <div className="absolute top-4 right-4 z-10">
              <span className="text-[10px] font-mono bg-black/60 text-white px-2 py-1 rounded-full border border-white/10">
                {item.year}
              </span>
            </div>
          )}
          {hasVideo && (
            <div className="absolute inset-0 z-[6] grid place-items-center pointer-events-none">
              <span className="relative flex h-16 w-16 md:h-20 md:w-20 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-[0_20px_50px_-15px_hsl(var(--primary)/0.9)] group-hover:scale-110 transition-transform duration-500">
                <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                <Play className="w-6 h-6 md:w-8 md:h-8 fill-current relative z-10 translate-x-0.5" />
              </span>
            </div>
          )}
          <div className="absolute bottom-4 right-4 z-10 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
            <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-white bg-primary/90 px-3 py-1.5 rounded-full">
              {hasVideo ? "Open" : "View"} <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-5 z-[5]">
            <div className="font-heading font-bold text-white text-lg md:text-xl leading-tight line-clamp-2">{item.title}</div>
            {item.subtitle && (
              <div className="mt-1 text-xs md:text-sm text-white/70 line-clamp-2">{item.subtitle}</div>
            )}
          </div>
        </div>

        <div className="p-4 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            {item.client && (
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground truncate">
                {item.client}
              </div>
            )}
            {item.tech_stack?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {item.tech_stack.slice(0, 3).map((t) => (
                  <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-foreground/8 text-foreground/70 border border-border/60">
                    {t}
                  </span>
                ))}
                {item.tech_stack.length > 3 && (
                  <span className="text-[10px] font-mono text-muted-foreground">+{item.tech_stack.length - 3}</span>
                )}
              </div>
            )}
          </div>
          {hasVideo ? (
            <Play className="w-4 h-4 fill-primary text-primary group-hover:scale-110 transition-transform shrink-0" />
          ) : (
            <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0" />
          )}
        </div>
      </Link>
    </motion.div>
  );
}
export default function Portfolio() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/portfolio-categories"),
      api.get("/portfolio"),
    ])
      .then(([c, i]) => { setCategories(c.data || []); setItems(i.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const catMap = useMemo(() => {
    const m = {};
    categories.forEach((c) => { m[c.slug] = c.name; });
    return m;
  }, [categories]);

  const filtered = useMemo(() => {
    if (active === "all") return items;
    return items.filter((i) => i.category_slug === active);
  }, [items, active]);

  const countFor = (slug) => (slug === "all" ? items.length : items.filter((i) => i.category_slug === slug).length);

  return (
    <div className="relative min-h-screen pt-24 md:pt-32" data-testid="portfolio-page">
      <Seo
        title="Portfolio · Selected work across AI, Web, Mobile & Research"
        description="A curated selection of CloudLab Technologies work — web applications, mobile apps, social media productions, AI models and research papers. Filter by category to explore."
        keywords="CloudLab portfolio, AI portfolio, web applications, mobile apps, AI models, research papers, CloudLab work"
      />
      <GlowOrb className="top-10 -left-20" color="primary" size={620} />
      <GlowOrb className="bottom-40 -right-20" color="secondary" size={620} />

      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 pb-10">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.25em] text-primary"
          >
            <Sparkles className="w-3.5 h-3.5" /> Portfolio
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-4 font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tight leading-[1.02]"
          >
            Selected work — <span className="text-primary">every</span> shape it takes.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.7 }}
            className="mt-5 text-lg text-muted-foreground max-w-2xl leading-relaxed"
          >
            Web platforms, native mobile apps, social-media productions, custom AI models and peer-reviewed research —
            hand-picked from ten years of engineering across the UK, Pakistan and the Gulf.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.5 }}
            className="mt-6"
          >
            <Link
              to="/case-studies"
              data-testid="portfolio-to-case-studies"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 border border-primary/30 bg-primary/5 hover:bg-primary/10 text-sm text-primary transition-colors"
            >
              <span className="font-mono uppercase tracking-[0.2em] text-[10px] opacity-80">New</span>
              <span className="font-medium">Read our engineering case studies</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>

        {/* Stats strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.6 }}
          className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
        >
          {[
            { k: items.length, l: "Selected pieces" },
            { k: categories.length, l: "Practice areas" },
            { k: "10+", l: "Years shipping" },
            { k: "40+", l: "Client teams" },
          ].map((s, i) => (
            <div key={i} className="rounded-2xl border border-border/70 bg-card/60 backdrop-blur p-5">
              <div className="font-heading font-black text-3xl md:text-4xl text-primary">{s.k}</div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{s.l}</div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Filter bar */}
      <section className="relative sticky top-24 z-30 py-4">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass rounded-full flex items-center gap-2 p-1.5 overflow-x-auto no-scrollbar">
            <LayoutGroup id="portfolio-cats">
              <div className="shrink-0 pl-3 pr-1 inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                <Filter className="w-3.5 h-3.5" /> Filter
              </div>
              <CategoryPill label="All" active={active === "all"} onClick={() => setActive("all")} count={countFor("all")} index={0} />
              {categories.map((c, i) => (
                <CategoryPill
                  key={c.slug}
                  label={c.name}
                  active={active === c.slug}
                  onClick={() => setActive(c.slug)}
                  count={countFor(c.slug)}
                  index={i + 1}
                />
              ))}
            </LayoutGroup>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative max-w-7xl mx-auto px-6 py-10 md:py-14 min-h-[400px]">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="rounded-3xl bg-foreground/5 aspect-[4/3] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 grid place-items-center">
              <Layers className="w-7 h-7 text-primary/70" />
            </div>
            <div className="mt-4 font-heading font-bold text-2xl">Nothing here yet.</div>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">
              We&apos;re curating more work in this category. In the meantime, check the other tabs — plenty to explore.
            </p>
          </motion.div>
        ) : (
          <LayoutGroup>
            <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              <AnimatePresence mode="popLayout">
                {filtered.map((item) => (
                  <PortfolioCard
                    key={item.id}
                    item={item}
                    categoryName={catMap[item.category_slug] || "—"}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          </LayoutGroup>
        )}
      </section>

      {/* Footer CTA */}
      <section className="relative max-w-7xl mx-auto px-6 pb-16">
        <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-secondary/[0.08] p-8 md:p-12 text-center">
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Working on something new?</div>
          <h3 className="mt-3 font-heading font-black text-3xl md:text-4xl">Let&apos;s build the next case study together.</h3>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/quote" className="rounded-full px-6 py-3 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center gap-2 shadow-[0_15px_40px_-15px_hsl(var(--primary)/0.6)]">
              Request a quote <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="rounded-full px-6 py-3 border border-border text-sm inline-flex items-center gap-2 hover:bg-foreground/5">
              Or say hello
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
